// The purchasing plan for the screens: shared/planning.ts over the synced tables and the forecast
// (lib/forecast.ts), worked out again only when one of them changes (or the day does) and shared by
// every component that asks. Also the edits
// Jeff makes on the dashboard: quantities typed over the suggestion and lines unticked (kept in this browser for a
// week), item settings, and draft POs straight from the plan (or lines added to the vendor's open draft).
// Make planning --- with the BOMs (a stock-free index, useBomStructure()) and the stock ledger the plan is also the
// MRP. Changes to rows that belong to items (stock, the ledger, POs and their lines, make orders, vendor links, item
// settings, overrides, B2B) plan only those items again (items in BOMs always are); anything else (items, vendors,
// BOMs, settings, a new forecast) plans everything, once per burst, in an idle moment, showing the last plan meanwhile.
import { useEffect, useRef, useSyncExternalStore } from "react";
import { coverDate, coverDays, coverOf } from "../../../shared/cover.ts"; // Cover through ---
import { DEFAULTS, type PlanCover, type Po, type PoLine } from "../../../shared/db.gen.ts";
import type { MutationArgs } from "../../../shared/mutations.ts";
import {
  type CoverTo,
  type ItemPlanResult,
  type Plan,
  type PlanData,
  type PlanItem,
  type PlanLink,
  type PlanVendor,
  planPurchasing,
  planToday,
} from "../../../shared/planning.ts";
import { lineDefaults } from "../../../shared/po.ts";
import { checkDraft, type DraftCheck, planBasis } from "../../../shared/po-plan-check.ts";
import { bomStructureNow, useBomStructure } from "./boms.ts";
import { changedKeys, forecastsNow, useForecasts } from "./forecast.ts";
import { mutate, mutateWithResult } from "./mutate.ts";
import { liveQty, type QtyEdit, raisedQty, readWork, staleEdits, supplyKey, writeWork } from "./plan-work.ts";
import { presaleProductsNow, reservationsNow, usePresaleProducts, useReservations } from "./presales.ts";
import { me } from "./session.ts";
import { type Change, store, useRows } from "./sync.ts";

type Inputs = ReturnType<typeof useInputs>;
let last: { inputs: Inputs; plan: Plan } | null = null;
let latest: Inputs | null = null;
let pending: (() => void) | null = null;
let version = 0;
const planListeners = new Set<() => void>();

function useInputs() {
  return [
    useForecasts(),
    useRows("item"),
    useRows("sales_summary"),
    useRows("stock_level"),
    useRows("location"),
    useRows("vendor"),
    useRows("vendor_item"),
    useRows("po"),
    useRows("po_line"),
    useRows("item_plan"),
    useRows("forecast_override"),
    useRows("b2b_demand"),
    useRows("planning_settings"),
    // Make planning ---
    useBomStructure(),
    useRows("stock_balance"),
    useRows("make_order"),
    useReservations(), // Pre-sales --- units of runs that aren't for sale (a change plans everything again)
    usePresaleProducts(), // Pre-sales --- products a pre-sale sells: planned whatever their title says (likewise)
    useRows("vendor_closure"), // Vendor closures --- days vendors don't work, added to lead times (a change plans everything)
    useRows("plan_cover"), // Cover through --- the person's days to buy to (a change plans everything)
  ] as const;
}

const dataOf = (inputs: Inputs, today: string): PlanData => {
  const [
    forecasts,
    items,
    sales,
    stock,
    locations,
    vendors,
    vendorItems,
    pos,
    poLines,
    itemPlans,
    overrides,
    b2b,
    settings,
    boms,
    balances,
    makeOrders,
    reservations,
    presaleProducts,
    closures,
    covers,
  ] = inputs;
  return {
    today,
    settings: settings[0],
    items,
    sales,
    stock,
    locations,
    vendors,
    vendorItems,
    pos,
    poLines,
    itemPlans,
    overrides,
    b2b,
    forecasts,
    boms,
    balances,
    makeOrders,
    reservations,
    presaleProducts,
    closures,
    coverTo: coverToOf(covers, today),
  };
};

// Cover through (migration 061, shared/cover.ts) ---------------------------------------------------------------
// Each person's own: how far ahead they're buying, for every vendor and per vendor (which wins). Their plan covers to
// then, and a draft they make keeps the day (po.cover_through), so its check plans with it whoever opens it.

/** The signed-in person's cover-through rows that are still ahead (a date that's passed counts for nothing). */
function myCovers(rows: readonly PlanCover[], today: string): PlanCover[] {
  const id = me()?.id;
  return rows.filter((r) => {
    // (One just made shows at once, before the server has said whose it is.)
    const c = !r.user_id || r.user_id === id ? coverOf(r) : null;
    return !!c && coverDays(c, today) >= 1;
  });
}

function coverToOf(rows: readonly PlanCover[], today: string): CoverTo | undefined {
  const mine = myCovers(rows, today);
  if (!mine.length) return undefined;
  let all: number | null = null;
  const byVendor = new Map<string, number>();
  for (const r of mine) {
    const days = coverDays(coverOf(r) as NonNullable<ReturnType<typeof coverOf>>, today);
    if (r.vendor_id) byVendor.set(r.vendor_id, days);
    else all = days;
  }
  return { all, byVendor };
}

/** The day a draft to this vendor is bought to last to (ISO): the person's day for it, else for every vendor, else null. */
export function coverThroughFor(vendorId: string): string | null {
  const today = planToday();
  const mine = myCovers(store.rows("plan_cover") as unknown as PlanCover[], today);
  const row = mine.find((r) => r.vendor_id === vendorId) ?? mine.find((r) => !r.vendor_id);
  const c = coverOf(row);
  return c ? coverDate(c, today) : null;
}

/**
 * The items whose inputs changed, when only rows that belong to items did (and the forecast only for those items);
 * null when something else changed and everything must be planned again.
 */
function changedItems(before: Inputs, after: Inputs): Set<string> | null {
  if (before[0].generation !== after[0].generation) return null;
  const only = new Set<string>();
  for (let i = 1; i < after.length; i++) {
    if (before[i] === after[i]) continue;
    if (i === 3) changedKeys(before[3], after[3], (r) => r.item_id, only);
    else if (i === 6) changedKeys(before[6], after[6], (r) => r.item_id, only);
    else if (i === 8) changedKeys(before[8], after[8], (r) => r.item_id, only);
    else if (i === 9) changedKeys(before[9], after[9], (r) => r.id, only);
    else if (i === 10) changedKeys(before[10], after[10], (r) => r.item_id, only);
    else if (i === 11) changedKeys(before[11], after[11], (r) => r.item_id, only);
    else if (i === 14) changedKeys(before[14], after[14], (r) => r.item_id, only);
    else if (i === 15)
      continue; // make orders only move items in BOMs, which are always planned again
    else if (i === 7) {
      // A PO's status or dates move every item on it.
      const ids = changedKeys(before[7], after[7], (r) => r.id, new Set());
      for (const l of [...before[8], ...after[8]]) if (ids.has(l.po_id)) only.add(l.item_id);
    } else return null;
  }
  return only;
}

/** Everything again, in an idle moment, once for however many changes arrive before it (from the latest inputs). */
function planLater() {
  if (pending) return;
  const run = () => {
    pending = null;
    if (!latest) return;
    last = { inputs: latest, plan: planPurchasing(dataOf(latest, planToday())) };
    version++;
    for (const fn of planListeners) fn();
  };
  const w = window as Window & { requestIdleCallback?: (fn: () => void, opts?: { timeout: number }) => number; cancelIdleCallback?: (id: number) => void };
  if (w.requestIdleCallback) {
    const id = w.requestIdleCallback(run, { timeout: 500 });
    pending = () => w.cancelIdleCallback?.(id);
  } else {
    const id = setTimeout(run, 30);
    pending = () => clearTimeout(id);
  }
}

export function usePlan(): Plan {
  useSyncExternalStore(
    (fn) => {
      planListeners.add(fn);
      return () => planListeners.delete(fn);
    },
    () => version,
  );
  const inputs = useInputs();
  latest = inputs;
  const today = planToday();
  if (!last || last.plan.today !== today) {
    pending?.();
    pending = null;
    last = { inputs, plan: planPurchasing(dataOf(inputs, today)) };
  } else if (last.inputs.some((x, i) => x !== inputs[i])) {
    const only = pending ? null : changedItems(last.inputs, inputs);
    if (only) last = { inputs, plan: planPurchasing(dataOf(inputs, today), { prev: last.plan, only }) };
    else planLater();
  }
  return last.plan;
}

// Check against the plan ---------------------------------------------------------------------------------
// A draft PO against the plan without it (shared/po-plan-check.ts), from the same inputs as usePlan(), reusing its plan
// (only the draft's items are planned again). Never worked out while rendering: <PlanCheckRunner> (rendered for open
// drafts only) schedules it in an idle moment after each change, so opening a PO (a sent one never plans at all) and
// typing never wait on it; the panel shows the last result meanwhile (`stale`). checkNow() works it out at once for a
// click that needs it (sending).

type CheckEntry = { inputs: Inputs; po: Po; lines: readonly PoLine[]; check: DraftCheck };
const checks = new Map<string, CheckEntry>();
const checkJobs = new Map<string, () => void>();
let checkIdle: (() => void) | null = null;
let checkVersion = 0;
const checkListeners = new Set<() => void>();
const notifyChecks = () => {
  checkVersion++;
  for (const fn of checkListeners) fn();
};

/** The plan's inputs straight from the store (the rows useRows() gives), outside React. */
const inputsNow = (): Inputs =>
  [
    forecastsNow(),
    ...(
      [
        "item",
        "sales_summary",
        "stock_level",
        "location",
        "vendor",
        "vendor_item",
        "po",
        "po_line",
        "item_plan",
        "forecast_override",
        "b2b_demand",
        "planning_settings",
      ] as const
    ).map((t) => store.rows(t)),
    bomStructureNow(),
    store.rows("stock_balance"),
    store.rows("make_order"),
    reservationsNow(),
    presaleProductsNow(),
    store.rows("vendor_closure"),
    store.rows("plan_cover"),
  ] as unknown as Inputs;

/** The plan for these inputs: usePlan()'s if it's for them, else planned again (only the changed items when it can). */
function planFor(inputs: Inputs): Plan {
  const today = planToday();
  if (last && last.plan.today === today && last.inputs.every((x, i) => x === inputs[i])) return last.plan;
  const only = last && last.plan.today === today ? changedItems(last.inputs, inputs) : null;
  const data = dataOf(inputs, today);
  pending?.();
  pending = null;
  last = { inputs, plan: last && only ? planPurchasing(data, { prev: last.plan, only }) : planPurchasing(data) };
  version++;
  for (const fn of planListeners) fn();
  return last.plan;
}

const sameRows = (a: readonly object[], b: readonly object[]) => a.length === b.length && a.every((x, i) => x === b[i]);

function computeCheck(po: Po, lines: readonly PoLine[]): DraftCheck {
  const inputs = inputsNow();
  const hit = checks.get(po.id);
  if (hit && hit.po === po && sameRows(hit.lines, lines) && hit.inputs.every((x, i) => x === inputs[i])) return hit.check;
  const plan = planFor(inputs);
  const check = checkDraft(po, lines, dataOf(inputs, plan.today), { plan });
  checks.delete(po.id);
  checks.set(po.id, { inputs, po, lines, check });
  if (checks.size > 12) checks.delete(checks.keys().next().value as string);
  return check;
}

function checkLater(poId: string, job: () => void, soon: boolean) {
  checkJobs.set(poId, job);
  if (checkIdle) return;
  const run = () => {
    checkIdle = null;
    const jobs = [...checkJobs.values()];
    checkJobs.clear();
    for (const fn of jobs) fn();
    notifyChecks();
  };
  const w = window as Window & { requestIdleCallback?: (fn: () => void, opts?: { timeout: number }) => number; cancelIdleCallback?: (id: number) => void };
  if (w.requestIdleCallback) {
    const id = w.requestIdleCallback(run, { timeout: soon ? 50 : 300 });
    checkIdle = () => w.cancelIdleCallback?.(id);
  } else {
    const id = setTimeout(run, soon ? 0 : 30);
    checkIdle = () => clearTimeout(id);
  }
}

/** Keeps an open draft's check up to date, in idle moments. Render it for drafts only; it draws nothing. */
export function PlanCheckRunner({ po, lines }: { po: Po; lines: readonly PoLine[] }): null {
  const current = useRef({ po, lines });
  current.current = { po, lines };
  // Any synced change (stock, POs, forecasts…): again when there's a moment, from the latest rows.
  useEffect(
    () =>
      store.subscribe(() => {
        const { po: p } = current.current;
        checkLater(p.id, () => computeCheck(current.current.po, current.current.lines), false);
      }),
    [],
  );
  // Opened, or its own lines changed: soon.
  useEffect(() => {
    checkLater(po.id, () => computeCheck(po, lines), true);
  }, [po, lines]);
  return null;
}

/** The draft's check (null until it's first worked out, and for POs it isn't for). `stale` while a newer one is on its way. */
export function usePlanCheck(po: Po | undefined, lines: readonly PoLine[]): { check: DraftCheck | null; stale: boolean } {
  useSyncExternalStore(
    (fn) => {
      checkListeners.add(fn);
      return () => checkListeners.delete(fn);
    },
    () => checkVersion,
  );
  if (po?.status !== "draft") return { check: null, stale: false };
  const hit = checks.get(po.id);
  return { check: hit?.check ?? null, stale: !hit || hit.po !== po || !sameRows(hit.lines, lines) || checkJobs.has(po.id) };
}

/** The draft's check worked out now if it isn't up to date (for a click that needs it, like sending). */
export function checkNow(po: Po, lines: readonly PoLine[]): DraftCheck | null {
  if (po.status !== "draft") return null;
  const check = computeCheck(po, lines);
  notifyChecks();
  return check;
}

// Quantities typed over the suggestion, and lines unticked ---------------------------------------------------
// Kept in this browser for a week (lib/plan-work.ts), so a reload or the new-version banner loses nothing; a typed
// quantity lasts until the item's open PO lines change. Every tab keeps the same work: a change in one reaches the
// others through the storage event, so no tab writes back what another changed.

const WORK_KEY = "hhl-ops.plan-work";
/** localStorage, or nothing when the browser blocks it (the work then lasts the tab). */
function saved(): string | null {
  try {
    return localStorage.getItem(WORK_KEY);
  } catch {
    return null;
  }
}
const loaded = readWork(saved(), Date.now());
let edits: ReadonlyMap<string, QtyEdit> = loaded.edits;
let unticked: ReadonlyMap<string, number> = loaded.unticked;
const listeners = new Set<() => void>();
const notify = () => {
  for (const fn of listeners) fn();
};
const emit = () => {
  try {
    localStorage.setItem(WORK_KEY, writeWork({ edits: new Map(edits), unticked: new Map(unticked) }));
  } catch {
    // Storage full or blocked: the work still lasts the session.
  }
  notify();
};
if (typeof window !== "undefined")
  window.addEventListener("storage", (e) => {
    if (e.key !== WORK_KEY) return;
    const work = readWork(e.newValue, Date.now());
    edits = work.edits;
    unticked = work.unticked;
    notify();
  });

/** The item's open PO lines as supplyKey() says them, worked out once per plan result. */
const supplyCache = new WeakMap<ItemPlanResult, string>();
const supplyOf = (r: ItemPlanResult) => {
  let key = supplyCache.get(r);
  if (key === undefined) supplyCache.set(r, (key = supplyKey(r.incoming)));
  return key;
};
const subscribeWork = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export function useQtyEdits(): ReadonlyMap<string, QtyEdit> {
  return useSyncExternalStore(subscribeWork, () => edits);
}

/** Types a quantity over the plan's suggestion (null: back to the suggestion). */
export function setQtyEdit(r: ItemPlanResult, qty: number | null) {
  const next = new Map(edits);
  if (qty === null) next.delete(r.item.id);
  else next.set(r.item.id, { qty, at: Date.now(), supply: supplyOf(r) });
  edits = next;
  emit();
}

/** Drops typed quantities whose item's open PO lines changed since (it was ordered, or something arrived). */
export function dropStaleEdits(plan: Plan) {
  const stale = staleEdits(edits, (id) => {
    const r = plan.byItem.get(id);
    return r && supplyOf(r);
  });
  if (!stale.length) return;
  const next = new Map(edits);
  for (const id of stale) next.delete(id);
  edits = next;
  emit();
}

export const qtyOf = (r: ItemPlanResult, e: ReadonlyMap<string, QtyEdit>) => liveQty(e.get(r.item.id), supplyOf(r)) ?? r.qty;
export const isEdited = (r: ItemPlanResult, e: ReadonlyMap<string, QtyEdit>) => liveQty(e.get(r.item.id), supplyOf(r)) !== undefined;

/** Items unticked on the plan (not to go on a PO), by item id with when. */
export function useUnticked(): ReadonlyMap<string, number> {
  return useSyncExternalStore(subscribeWork, () => unticked);
}

export function setTicked(itemIds: readonly string[], on: boolean) {
  const next = new Map(unticked);
  for (const id of itemIds) on ? next.delete(id) : next.set(id, Date.now());
  unticked = next;
  emit();
}

// Item settings and global settings ------------------------------------------------------------------------

type ItemPlanPatch = MutationArgs<"item_plan.update">["patch"];

/** Saves an item's planning settings, making its row the first time. */
export function saveItemPlan(itemId: string, patch: ItemPlanPatch) {
  return store.row("item_plan", itemId) ? mutate("item_plan.update", { id: itemId, patch }) : mutate("item_plan.create", { id: itemId, ...patch });
}

export function saveSettings(patch: MutationArgs<"planning_settings.update">["patch"]) {
  const row = (store.rows("planning_settings") as unknown as { id: string }[])[0];
  return row ? mutate("planning_settings.update", { id: row.id, patch }) : mutate("planning_settings.create", { id: crypto.randomUUID(), ...patch });
}

// Draft POs from the plan ------------------------------------------------------------------------------

/**
 * A line for a draft from the plan. Check against the plan --- `result` (and `planQty`, the plan's own quantity when
 * someone typed over it) is kept on the line as its plan_basis, so the check can say what changed since.
 */
export type DraftLine = { item: PlanItem; link: PlanLink | null; qty: number; result?: ItemPlanResult; planQty?: number };

/**
 * One draft PO for a vendor with these lines (po.create, like the PO screens, without opening it). The
 * PO and its lines show at once, so the plan counts them as incoming straight away.
 * Make planning --- `shipTo` (a CM for consigned parts) and `kind` (make for a build: po.create_make) come from the plan.
 */
export async function createDraftPo(
  vendor: PlanVendor,
  lines: DraftLine[],
  opts: { shipTo?: string | null; kind?: "purchase" | "make"; runId?: string | null } = {},
): Promise<{ id: string; number: string } | null> {
  const id = crypto.randomUUID();
  const now = new Date().toISOString();
  // A build's made-up link (no CM price yet) isn't a vendor link: the server fills the line from nothing.
  const real = (l: DraftLine) => (l.link && !l.link.id.startsWith("make:") ? l.link : undefined);
  const today = planToday();
  const basisOf = (l: DraftLine) => (l.result ? { plan_basis: planBasis(l.result, today, { via: "plan", qty: l.planQty ?? l.result.qty }) } : {});
  const args = lines.map((l, i) => ({
    id: crypto.randomUUID(),
    item_id: l.item.id,
    qty: l.qty,
    position: i + 1,
    ...lineDefaults(l.item, real(l), l.qty),
    ...basisOf(l),
  }));
  const kind = opts.kind ?? "purchase";
  const cover = kind === "make" ? null : coverThroughFor(vendor.id); // Cover through --- builds follow the Make plan
  const changes: Change[] = [
    {
      t: "po",
      id,
      row: {
        ...DEFAULTS.po,
        id,
        number: "PO-…",
        vendor_id: vendor.id,
        currency: vendor.currency,
        status: "draft",
        kind,
        ship_to_location_id: opts.shipTo ?? null,
        cover_through: cover,
        created_at: now,
        updated_at: now,
      },
    },
    ...args.map((a) => ({ t: "po_line", id: a.id, row: { ...DEFAULTS.po_line, ...a, po_id: id, created_at: now, updated_at: now } })),
  ];
  // A build goes through consignment's po.create_make: a make PO whose lines get their make orders (BOM snapshots)
  // at the CM's site. Its lines carry the ids made here, so they show (and the plan counts them) at once.
  const { ok, result } =
    kind === "make"
      ? await mutateWithResult(
          "po.create_make",
          {
            id,
            vendor_id: vendor.id,
            ...(opts.shipTo ? { ship_to_location_id: opts.shipTo } : {}),
            ...(opts.runId ? { production_run_id: opts.runId } : {}), // Production runs --- the run this build is for
            lines: args.map((a) => ({
              id: crypto.randomUUID(),
              line_id: a.id,
              item_id: a.item_id,
              qty: a.qty,
              ...("plan_basis" in a ? { plan_basis: a.plan_basis } : {}),
            })),
          },
          changes,
        )
      : await mutateWithResult(
          "po.create",
          {
            id,
            vendor_id: vendor.id,
            kind,
            ...(opts.shipTo ? { ship_to_location_id: opts.shipTo } : {}),
            ...(cover ? { cover_through: cover } : {}),
            lines: args,
          },
          changes,
        );
  if (!ok) return null;
  forgetEdits(lines);
  return { id, number: (result as { number?: string } | undefined)?.number ?? "" };
}

function forgetEdits(lines: readonly DraftLine[]) {
  const next = new Map(edits);
  for (const l of lines) next.delete(l.item.id);
  edits = next;
  emit();
}

/**
 * Puts plan lines on an open draft (openDraftFor(): the vendor's, for this ship-to and kind) instead of starting one, in
 * one mutation that the server refuses once the draft has been sent (po.add_from_plan): new items go after its last line
 * with their plan basis; an item already on it is raised to cover what the plan still needs: the line's qty plus the
 * need, rounded by the vendor's MOQ and multiple once for the whole line (the suggestion's own rounding would add a
 * second MOQ), or plus a quantity typed over the suggestion as typed. Not for make drafts (builds go through the Make plan).
 */
export async function addToDraftPo(po: Po, lines: DraftLine[]): Promise<{ added: number; raised: number } | null> {
  const onIt = new Map<string, PoLine>();
  let last = 0;
  for (const l of store.rows("po_line") as unknown as PoLine[]) {
    if (l.po_id !== po.id) continue;
    last = Math.max(last, l.position);
    if (!onIt.has(l.item_id)) onIt.set(l.item_id, l);
  }
  const fresh = lines.filter((l) => !onIt.has(l.item.id));
  const today = planToday();
  const now = new Date().toISOString();
  const basisOf = (l: DraftLine, qty: number) => (l.result ? { plan_basis: planBasis(l.result, today, { via: "plan", qty }) } : {});
  const args = fresh.map((l, i) => ({
    id: crypto.randomUUID(),
    item_id: l.item.id,
    qty: l.qty,
    position: last + i + 1,
    ...lineDefaults(l.item, l.link && !l.link.id.startsWith("make:") ? l.link : undefined, l.qty),
    ...basisOf(l, l.planQty ?? l.result?.qty ?? l.qty),
  }));
  // Cover through --- the draft now lasts to the person's day for this vendor (or the plan's usual).
  const cover = coverThroughFor(po.vendor_id);
  const covered = { ...po, cover_through: cover };
  // An item already on it: the plan without this draft says what its line should be (as "Check against the plan" and its
  // Update do), and its basis is that plan's, so the check reads it as the plan's (or a typed quantity as by hand).
  const check =
    fresh.length < lines.length
      ? checkNow(
          covered,
          [...(store.rows("po_line") as unknown as PoLine[])].filter((x) => x.po_id === po.id),
        )
      : null;
  const raise = lines.flatMap((l) => {
    const line = onIt.get(l.item.id);
    if (!line) return [];
    const row = check?.rows.find((x) => x.itemId === l.item.id);
    const typed = !l.result || l.qty !== l.result.qty;
    // The item's total on the draft moves to the plan's (one line raised by the difference, as the check's Update does,
    // so an item on two lines isn't ordered twice over); a typed quantity adds as typed.
    const qty = typed
      ? Number(line.qty) + l.qty
      : row?.plan != null
        ? Number(line.qty) + (row.plan - row.drafted)
        : raisedQty(Number(line.qty), l.qty, l.result);
    const basisFrom = row?.result ?? l.result;
    // The basis is the plan's for this line: its share of the plan's total (what the check compares the line with).
    const planQty = row?.plan != null ? Number(line.qty) + (row.plan - row.drafted) : qty;
    const basis = basisFrom ? planBasis(basisFrom, today, { via: "plan", qty: planQty, poId: po.id }) : undefined;
    return qty > Number(line.qty) ? [{ line, l, qty, basis }] : [];
  });
  const changes: Change[] = [
    ...args.map((a) => ({ t: "po_line", id: a.id, row: { ...DEFAULTS.po_line, ...a, po_id: po.id, created_at: now, updated_at: now } })),
    ...raise.map(({ line, qty }) => ({ t: "po_line", id: line.id, row: { ...line, qty, auto_qty: null, updated_at: now } })),
    ...(cover !== (po.cover_through?.slice(0, 10) ?? null) ? [{ t: "po" as const, id: po.id, row: covered }] : []),
  ];
  const { ok, result } = await mutateWithResult(
    "po.add_from_plan",
    {
      po_id: po.id,
      lines: args,
      raise: raise.map(({ line, l, qty, basis }) => ({ line_id: line.id, item_id: l.item.id, qty, ...(basis ? { plan_basis: basis } : {}) })),
      cover_through: cover,
    },
    changes,
  );
  if (!ok) return null;
  forgetEdits(lines);
  const res = result as { added: number; raised: number };
  return { added: res.added, raised: res.raised };
  // (A raise the check says isn't needed any more is left out; the toast says nothing was added then.)
}
